import {deGlass, serGlass} from "../glass/MdGlass";
import {Opening} from "../../../../../../api/Openings";
import {inches} from "../../const";
import {seedDefaultJambAnchors} from "./MdDefaultAnchors";
import {UnitSystem} from "../../../../../../api/Projects";
import {HardwareGroupWithContent} from "../../../../../../api/Hardware";
import {parseDrawingUnits, prepConfigDrawingUnits} from "../../util/DrawingUnits";
import {FrmVar} from "../../drawfrm/FrmVar";
import {MIN_FLEX_LENGTH} from "../../drawfrm/FrmShape";
import {
    MdFrameBlock,
    MdFrameBlockChild,
    MdFrameItem,
    MdFrameStick,
    MdFrameJoint,
    MdStickJoint,
    MdCornerInfo,
    MdStepInfo,
    MdFrameLite,
    MdFrameDoors,
    shareJoinedStepSizes,
} from "./MdFrameBlock";
import {convertLegacyFrame, LegacyInfo, LegacyPerimeter} from "./MdFrameLegacy";
import {EventEmitter2} from "../../util/EventEmitter2";
import {serFrmVar, restoreFrmVar, serScalar, restoreScalar} from "../../serialize/frmVar";
import {
    FrameDesign,
    SerFrameBlock,
    SerFrameBlockChild,
    SerFrameItem,
    SerFrameStick,
    SerLegacyFramePerimeter,
    SerFrameProfileDetail,
    SerScreenSlot,
} from "../../../../../../api/elevationTool/ScreenElevationData";
import {
    MdFrameProfileDetail,
    DEFAULT_BOTTOM_RABBET_DEPTH,
    DEFAULT_DRYWALL_LIP,
    DEFAULT_DRYWALL_HOOK_DROP,
    DEFAULT_KERF_DEPTH,
    DEFAULT_EXP_FACE,
} from "./MdFrameProfileDetail";

export type MdScreenSlot = SerScreenSlot;

// a frame lite's reference mark: the panel prefix and its number.
const frameMarkFormat = /^P\d+$/;

export class MdFrameSpec {
    profile: MdFrameProfileType = "STD";
    handing: string = "";
    locationTransition: "to" | "from" | "" = "";
    isPair: boolean = false;
    flipDoubleActiveHanding: boolean = false;

    // when adding a FrmVar here, also update shared-2d/Properties.tsx to include the property

    // the opening's own nominal width — for a pair that's the active leaf's share, with
    // inActiveDoorWidth carrying the other. These two are the editable source of the
    // frame's width; openingWidth below is their sum. Both map 1:1 onto the opening
    // columns and are written back on save (see ElevationApp.nominalDimensions).
    nominalWidth = new FrmVar("frameSpec.nominalWidth", 30 * inches);

    openingWidth = new FrmVar("frameSpec.openingWidth", 30 * inches);
    activeDoorWidth = new FrmVar("frameSpec.activeDoorWidth", 30 * inches).overrideIsModified(true);
    inActiveDoorWidth = new FrmVar("frameSpec.inActiveDoorWidth", 1 * inches).overrideIsModified(true);
    pairDoorGap = new FrmVar("frameSpec.pairDoorGap", 1/8 * inches).overrideIsModified(true);

    jambDepth = new FrmVar("frameSpec.jambDepth", 4 * inches).overrideIsModified(true);

    frameStopToDoorGap = new FrmVar("frameSpec.frameStopToDoorGap", 1/8 * inches);
    frameJambToDoorGap = new FrmVar("frameSpec.frameJambToDoorGap", 1/8 * inches);
    jambFace = new FrmVar("frameSpec.jambFace", 2 * inches);
    thresholdHeight = new FrmVar("frameSpec.thresholdHeight", 1 * inches);
    throatSize = new FrmVar("frameSpec.throatSize", 4 * inches);

    // the profile-specific cut dimensions (rabbet, tabs, drywall/kerf/exp, doubleEgress,
    // doorThickness) that define the door frame's cross-section. Everything the
    // FrameProfile* drawings read except the frame-wide jambDepth / jambFace above.
    doorFrameProfile = new MdFrameProfileDetail(this.jambFace);

    // door size to door-connection part of jamb
    innerWidth = new FrmVar("frameSpec.innerWidth", 30 * inches);
    nominalHeight = new FrmVar("frameSpec.nominalHeight", 7 * 12 * inches);

    frameBlockId = 0;
    frameBlocks: MdFrameBlock = {
        id: "base",
        kind: "column",
        size: new FrmVar("base-size", 0),
        children: [],
    };

    frameBlockJointDefault = new EventEmitter2<MdFrameJoint>("miter", "frame.blockJointDeafult");

    // door slots of a multi-opening screen (see SerScreenSlot); empty on a single-door screen.
    slots: MdScreenSlot[] = [];
    // name of the slot the primary opening fills; undefined on a single-door screen.
    primarySlot?: string;

    // the clear size of the opening that fills a secondary door slot (drawing units), or null
    // for an empty slot. Set by ElevationApp; not part of the design.
    slotOpeningSize: (slot: string) => {width: number; nominalHeight: number} | null = () => null;

    // every slot a secondary opening can fill: all slots but the primary's.
    secondarySlotNames(): string[] {
        return this.slots.filter(s => s.name !== this.primarySlot).map(s => s.name);
    }
    frameBlocksChanged = new EventEmitter2<boolean>(false, "frame.blocksChanged");

    // runtime cache of per-stick joint info (cross/tee), keyed by stick id. Filled
    // by the grid view after each layout (positions aren't in the model); the
    // Selected Stick ribbon reads it to offer the cross flip / show tee state.
    jointInfo: Map<string, MdStickJoint> = new Map();

    // runtime cache of resolved block/cell sizes (px), keyed by model id. Filled
    // after each layout. Structural edits (e.g. a cross flip) read it to seed
    // sensible sizes on the blocks they create, so the layout doesn't jump.
    sizeInfo: Map<string, {width: number; height: number}> = new Map();

    // the outer corners of the laid-out frame outline, by key. Runtime only, computed
    // after each layout (see scenes/frameGrid/Corners.ts).
    cornerInfo: MdCornerInfo[] = [];

    // the laid-out size of each step, by void cell id. Runtime only, computed after each
    // layout.
    stepInfo: Map<string, MdStepInfo> = new Map();

    // what part of the outline each outline stick covered at the last render, by stick id
    // (see BlockOps.outlineStickCoverage). Runtime only: an anchored stick whose coverage
    // changed starts its anchors over (see Anchors.normalizeAnchors).
    anchorCoverage: Map<string, string> = new Map();

    undercutFrame = new FrmVar("frameSpec.undercutFrame", 0);

    // every lite cell in the block tree, in drawing order (a block's children left to
    // right / top to bottom, depth first).
    lites(): MdFrameLite[] {
        const out: MdFrameLite[] = [];

        const walk = (block: MdFrameBlock) => {
            for(const child of block.children) {
                if(child.kind === "item" && child.item.kind === "lite") out.push(child.item);
                if(child.kind === "block") walk(child.block);
            }
        };

        walk(this.frameBlocks);
        return out;
    }

    // give a mark to every lite that has none — a newly added cell, a fixed panel just
    // converted to a lite, or a blob written before marks existed. Each takes the lowest
    // unused mark, so a removed lite frees its number for the next one added and a lite
    // that already has one never renumbers. Call after any change to the block tree (see
    // the selectedTab emit helpers) and at load.
    //
    // Frame lites are marked P1, P2, … — a door lite's G1 and a frame panel's P1 sit on
    // the same submittal, so the prefix says which drawing a row came from. A mark in any
    // other form is dropped and re-assigned, which re-marks a lite saved by a build that
    // numbered frame lites with the door prefix.
    assignLiteMarks() {
        const lites = this.lites();
        for(const lite of lites) {
            if(lite.mark && !frameMarkFormat.test(lite.mark)) lite.mark = undefined;
        }

        const used = new Set(lites.map(l => l.mark).filter(v => !!v));

        for(const lite of lites) {
            if(lite.mark) continue;

            for(let i = 1; ; i++) {
                const mark = "P" + i;
                if(used.has(mark)) continue;

                used.add(mark);
                lite.mark = mark;
                break;
            }
        }
    }

    // switch the frame profile and reset the profile-dependent cut properties to the
    // new profile's defaults (wiping any user overrides on them), sharing the reset
    // logic with fromOpening. Emits so calculated dependents (e.g. throatSize)
    // re-derive; the caller must still rebuild the active scene (app.showView) so the
    // new profile cross-section draws.
    setProfile(profile: MdFrameProfileType) {
        this.profile = profile;
        applyProfileDefaults(this, true);
    }

    // anchorProduct seeds the side jamb anchors of a new frame (see MdDefaultAnchors); null = none.
    static fromOpening(opening: Opening, hardware: HardwareGroupWithContent | null, sys: UnitSystem, anchorProduct: number | null = null) {
        const spec = new MdFrameSpec();

        spec.profile = opening.frameProfile as any || "STD";

        // import the persisted frame undercut from the hardware group's prep config.
        // Set before the door-height calc below, which derives from it.
        const undercutFrame = prepConfigDrawingUnits(hardware, "underCutFrame");
        if(undercutFrame !== null) spec.undercutFrame.setInitial(undercutFrame);

        spec.locationTransition = opening.locationTransition;
        spec.isPair = opening.openingType === "Pair";

        let defaultHanding = "LH"
        if(spec.isPair) {
            defaultHanding = "LHA"
        }

        spec.handing = opening.handing || defaultHanding

        spec.flipDoubleActiveHanding = opening.flipDoubleActiveHanding;

        spec.jambDepth.setInitial(parseDrawingUnits(opening.jambDepth || "5\"", "imperial")); // jamb depth is always in imperial
        spec.doorFrameProfile.doorThickness.setInitial(parseDrawingUnits(opening.doorThickness, sys) || (1 + 3/4) * inches);
        // profile-dependent cut defaults (rabbet, tabs, drywall/kerf/exp). Shared with
        // setProfile so switching profiles behaves like a fresh load. Silent here — the
        // spec is still being constructed (no scene/calculated deps wired yet).
        applyProfileDefaults(spec, false);

        if(spec.isPair) {
            spec.inActiveDoorWidth.setInitial(parseDrawingUnits(opening.inactiveDoorWidth, sys));
        }

        spec.nominalWidth.setInitial(parseDrawingUnits(opening.nominalWidth, sys));

        // the clear opening spans both leaves on a pair. Calculated off the two nominal
        // vars rather than seeded from them, so editing either (the frame grid's nominal
        // dimensions, the Properties dialog) reflows the frame.
        spec.openingWidth.createCalculated((props) => {
            if(spec.isPair) return props.nominalWidth + props.inactiveDoorWidth;
            return props.nominalWidth;
        }, {
            nominalWidth: spec.nominalWidth,
            inactiveDoorWidth: spec.inActiveDoorWidth,
        }, spec.isPair ? "nominalWidth + inactiveDoorWidth" : "nominalWidth")

        spec.throatSize.createCalculated((props) => {
            return props.jambDepth - (props.topTab + props.bottomTab);
        }, {
            jambDepth: spec.jambDepth,
            topTab: spec.doorFrameProfile.topTab,
            bottomTab: spec.doorFrameProfile.bottomTab
        }, "jambDepth - (topTab + bottomTab)")

        // the clear opening the frame surrounds. openingWidth already spans both
        // leaves for a pair (active + inactive, see the calc above), so the inner
        // width is just openingWidth in every case — adding the inactive leaf and
        // pair gap again here double-counted them (76" opening drew as 112 1/8").
        spec.innerWidth.createCalculated((props) => {
            return props.openingWidth;
        }, {
            openingWidth: spec.openingWidth,
        }, "openingWidth")

        spec.nominalHeight.setInitial(parseDrawingUnits(opening.nominalHeight, sys));

        spec.activeDoorWidth.createCalculated((props) => {
            const inner = props.openingWidth - (props.frameStopToDoorGap + props.frameJambToDoorGap);
            if(spec.isPair) {
                console.log(`active-width: ${inner / inches} - ${props.inactiveDoorWidth / inches} - ${props.pairDoorGap / inches}`)
                return inner - props.inactiveDoorWidth - props.pairDoorGap;
            }

            return inner;
        }, {
            openingWidth: spec.openingWidth,
            frameStopToDoorGap: spec.frameStopToDoorGap,
            frameJambToDoorGap: spec.frameJambToDoorGap,
            inactiveDoorWidth: spec.inActiveDoorWidth,
            pairDoorGap: spec.pairDoorGap,
        }, spec.isPair
            ? "openingWidth - (frameStopToDoorGap + frameJambToDoorGap) - inactiveDoorWidth - pairDoorGap"
            : "openingWidth - (frameStopToDoorGap + frameJambToDoorGap)");

        // the frame around a single door, as it is built: the head over a row of left
        // jamb, door cell and right jamb. The jambs run to the floor; the door cell
        // stacks the doors on the threshold (its sill). See inline-outline-plan.md.
        const head: MdFrameStick = {id: `stick-${spec.frameBlockId++}`};
        const leftJamb: MdFrameStick = {id: `stick-${spec.frameBlockId++}`};
        const rightJamb: MdFrameStick = {id: `stick-${spec.frameBlockId++}`};

        const doorCell: MdFrameBlock = {
            id: "door",
            kind: "column",
            size: new FrmVar("door:size", 0),
            children: [
                {kind: "item", item: {id: "doors", kind: "doors", width: spec.innerWidth, height: spec.nominalHeight}},
                {kind: "item", item: {id: "threshold", kind: "threshold", size: spec.thresholdHeight}},
            ],
        };

        const doorRow: MdFrameBlock = {
            id: "base-row",
            kind: "row",
            size: new FrmVar("base-row:size", 0),
            children: [
                {kind: "stick", stick: leftJamb},
                {kind: "block", block: doorCell},
                {kind: "stick", stick: rightJamb},
            ],
        };

        spec.frameBlocks = {
            id: "base",
            kind: "column",
            size: new FrmVar("base:size", 0),
            children: [
                {kind: "stick", stick: head},
                {kind: "block", block: doorRow},
            ],
        };

        if(anchorProduct !== null) seedDefaultJambAnchors(leftJamb, rightJamb, opening, sys, anchorProduct);

        wireDoorsItem(spec);

        return spec;
    }

    // serialize the frame Design fields (Opening-derived / Calc / Runtime fields are
    // omitted — see ../../data-json-dtos.md). `profile` lives on the envelope identity,
    // not here.
    toDesign(): FrameDesign {
        return {
            pairDoorGap: serFrmVar(this.pairDoorGap),
            frameStopToDoorGap: serFrmVar(this.frameStopToDoorGap),
            frameJambToDoorGap: serFrmVar(this.frameJambToDoorGap),
            jambFace: serFrmVar(this.jambFace),
            thresholdHeight: serFrmVar(this.thresholdHeight),
            doorFrameProfile: serProfileDetail(this.doorFrameProfile),
            undercutFrame: serFrmVar(this.undercutFrame),
            frameBlockId: this.frameBlockId,
            frameBlocks: serBlock(this.frameBlocks),
            frameBlockJointDefault: this.frameBlockJointDefault.value,
            primarySlot: this.primarySlot,
            slots: this.slots.length > 0 ? this.slots.map(s => ({...s})) : undefined,
        };
    }

    // rebuild a spec from a stored Design: start from fromOpening (which re-applies the
    // opening dimensions and wires the calculated vars) then overlay the stored design —
    // scalar vars, then the layout tree with the doors item re-bound to the live vars.
    static fromDesign(design: FrameDesign, opening: Opening, hardware: HardwareGroupWithContent | null, sys: UnitSystem): MdFrameSpec {
        const spec = MdFrameSpec.fromOpening(opening, hardware, sys);

        restoreFrmVar(spec.pairDoorGap, design.pairDoorGap);
        restoreFrmVar(spec.frameStopToDoorGap, design.frameStopToDoorGap);
        restoreFrmVar(spec.frameJambToDoorGap, design.frameJambToDoorGap);
        restoreFrmVar(spec.jambFace, design.jambFace);
        restoreFrmVar(spec.thresholdHeight, design.thresholdHeight);
        restoreProfileDetail(spec.doorFrameProfile, design.doorFrameProfile);
        restoreFrmVar(spec.undercutFrame, design.undercutFrame);

        spec.frameBlockId = design.frameBlockId ?? 0;
        spec.frameBlockJointDefault.emit(design.frameBlockJointDefault);
        const legacy: LegacyInfo = {perimeters: new Map(), thresholdSticks: new Set()};
        spec.frameBlocks = convertLegacyFrame(spec, deBlock(design.frameBlocks, legacy), legacy);
        shareJoinedStepSizes(spec.frameBlocks);
        spec.primarySlot = design.primarySlot;
        // a slot whose cell is no longer a doors item is unplaced.
        spec.slots = (design.slots ?? []).map(s => ({
            ...s,
            cell: s.cell && findItemById(spec.frameBlocks, s.cell)?.kind === "doors" ? s.cell : null,
        }));
        wireDoorsItem(spec);
        spec.assignLiteMarks();

        return spec;
    }
}

// bind (or re-bind) the doors and threshold items to the live spec vars. The door
// width follows the calculated innerWidth. The threshold's height is thresholdHeight.
// The doors cell is nominalHeight − undercutFrame − thresholdHeight tall, so the door
// cell (doors over threshold) reaches the frame's floor line: the jambs are cut down by
// undercutFrame, and the true floor is that far below them (DoorNominalDimensions). The
// door leaf itself draws down over the threshold (see FrameBlockItem). Shared by
// fromOpening (default tree) and fromDesign (stored tree) so both wire the door
// identically.
//
// A secondary door slot (multi-opening screen) takes the size of the opening that fills it: its
// clear width, and its own nominal height less the frame's undercut and threshold. An empty slot
// keeps its design width and shares the primary's door height.
export function wireDoorsItem(spec: MdFrameSpec) {
    for(const threshold of findItems(spec.frameBlocks, "threshold")) {
        if(threshold.kind === "threshold") threshold.size = spec.thresholdHeight;
    }

    const height = new FrmVar("doors:height", spec.nominalHeight.get()).createCalculated(
        (p) => p.nominalHeight - p.undercutFrame - p.thresholdHeight,
        {
            nominalHeight: spec.nominalHeight,
            undercutFrame: spec.undercutFrame,
            thresholdHeight: spec.thresholdHeight,
        },
        "nominalHeight - undercutFrame - thresholdHeight",
    );

    const primary = primaryDoorsItem(spec);
    for(const item of findItems(spec.frameBlocks, "doors")) {
        if(item.kind !== "doors") continue;

        if(item === primary) {
            item.width = spec.innerWidth;
            item.height = height;
            continue;
        }

        const slot = slotOfCell(spec, item.id);
        const size = slot ? spec.slotOpeningSize(slot.name) : null;
        if(!size) {
            item.height = height;
            continue;
        }

        // the saved width follows the opening too, so an emptied slot keeps the last size.
        item.width.set(size.width, {debug: "slot-opening-width"});
        const nominalHeight = new FrmVar(item.id + ":nominalHeight", size.nominalHeight);
        item.height = new FrmVar(item.id + ":height", size.nominalHeight).createCalculated(
            (p) => p.nominalHeight - p.undercutFrame - p.thresholdHeight,
            {
                nominalHeight: nominalHeight,
                undercutFrame: spec.undercutFrame,
                thresholdHeight: spec.thresholdHeight,
            },
            "slot opening nominalHeight - undercutFrame - thresholdHeight",
        );
    }
}

// the doors item the primary opening fills: the cell of the primary slot on a multi-opening
// screen (null while that slot is unplaced), else the only doors item.
export function primaryDoorsItem(spec: MdFrameSpec): MdFrameDoors | null {
    const item = spec.slots.length === 0
        ? findItem(spec.frameBlocks, "doors")
        : findItemById(spec.frameBlocks, spec.slots.find(s => s.name === spec.primarySlot)?.cell ?? "");

    return item?.kind === "doors" ? item : null;
}

// the slot a doors item holds, or undefined.
export function slotOfCell(spec: MdFrameSpec, cell: string): MdScreenSlot | undefined {
    return spec.slots.find(s => s.cell === cell);
}

// true when the doors item is a secondary door slot: any doors item but the primary's.
export function isSecondaryDoors(spec: MdFrameSpec, item: MdFrameItem): boolean {
    return item.kind === "doors" && item !== primaryDoorsItem(spec);
}

// every item of `kind` in the tree (depth-first).
function findItems(block: MdFrameBlock, kind: MdFrameItem["kind"]): MdFrameItem[] {
    const out: MdFrameItem[] = [];
    for(const c of block.children) {
        if(c.kind === "item" && c.item.kind === kind) out.push(c.item);
        if(c.kind === "block") out.push(...findItems(c.block, kind));
    }
    return out;
}

// the item with this id in the tree (depth-first), or null.
export function findItemById(block: MdFrameBlock, id: string): MdFrameItem | null {
    for(const c of block.children) {
        if(c.kind === "item" && c.item.id === id) return c.item;
        if(c.kind === "block") {
            const found = findItemById(c.block, id);
            if(found) return found;
        }
    }
    return null;
}

// the first item of `kind` in the tree (depth-first), or null.
function findItem(block: MdFrameBlock, kind: MdFrameItem["kind"]): MdFrameItem | null {
    for(const c of block.children) {
        if(c.kind === "item" && c.item.kind === kind) return c.item;
        if(c.kind === "block") {
            const found = findItem(c.block, kind);
            if(found) return found;
        }
    }
    return null;
}

// --- frame Design (de)serialization ---

function serProfileDetail(p: MdFrameProfileDetail): SerFrameProfileDetail {
    return {
        rabbetDepth: serFrmVar(p.rabbetDepth),
        bottomRabbetDepth: serFrmVar(p.bottomRabbetDepth),
        topTab: serFrmVar(p.topTab),
        bottomTab: serFrmVar(p.bottomTab),
        doubleEgressLip: serFrmVar(p.doubleEgressLip),
        doubleEgressStep1: serFrmVar(p.doubleEgressStep1),
        doubleEgressStep2: serFrmVar(p.doubleEgressStep2),
        drywallLip: serFrmVar(p.drywallLip),
        drywallHookDrop: serFrmVar(p.drywallHookDrop),
        kerfDepth: serFrmVar(p.kerfDepth),
        expFace: serFrmVar(p.expFace),
    };
}

// doubleEgressSmallFace is a calc var, so restoring the steps re-derives it — nothing
// to restore for it directly. doorThickness is Opening-derived (set by fromOpening).
function restoreProfileDetail(p: MdFrameProfileDetail, s: SerFrameProfileDetail) {
    restoreFrmVar(p.rabbetDepth, s.rabbetDepth);
    restoreFrmVar(p.bottomRabbetDepth, s.bottomRabbetDepth);
    restoreFrmVar(p.topTab, s.topTab);
    restoreFrmVar(p.bottomTab, s.bottomTab);
    restoreFrmVar(p.doubleEgressLip, s.doubleEgressLip);
    restoreFrmVar(p.doubleEgressStep1, s.doubleEgressStep1);
    restoreFrmVar(p.doubleEgressStep2, s.doubleEgressStep2);
    restoreFrmVar(p.drywallLip, s.drywallLip);
    restoreFrmVar(p.drywallHookDrop, s.drywallHookDrop);
    restoreFrmVar(p.kerfDepth, s.kerfDepth);
    restoreFrmVar(p.expFace, s.expFace);
}

// restore a fresh distance FrmVar from its wire form.
function deVar(name: string, s: {in: number; mod?: boolean}): FrmVar {
    const v = new FrmVar(name, 0);
    restoreFrmVar(v, s);
    return v;
}

function deScalar(name: string, s: {value: number; mod?: boolean}): FrmVar {
    const v = new FrmVar(name, 0);
    restoreScalar(v, s);
    return v;
}

function serBlock(b: MdFrameBlock): SerFrameBlock {
    return {
        id: b.id,
        kind: b.kind,
        size: serFrmVar(b.size),
        children: b.children.map(serChild),
    };
}

// `legacy` collects what a blob written before the outline sticks moved into the tree
// carries (a block's `perimeter`, threshold-variant sticks), for convertLegacyFrame.
function deBlock(b: SerFrameBlock, legacy: LegacyInfo): MdFrameBlock {
    if(b.perimeter) legacy.perimeters.set(b.id, deLegacyPerimeter(b.perimeter, legacy));
    return {
        id: b.id,
        kind: b.kind,
        size: deVar(b.id + ":size", b.size),
        children: b.children.map(c => deChild(c, legacy)),
    };
}

function serChild(c: MdFrameBlockChild): SerFrameBlockChild {
    if(c.kind === "item") return {kind: "item", item: serItem(c.item)};
    if(c.kind === "block") return {kind: "block", block: serBlock(c.block)};
    return {kind: "stick", stick: serStick(c.stick)};
}

function deChild(c: SerFrameBlockChild, legacy: LegacyInfo): MdFrameBlockChild {
    if(c.kind === "item") return {kind: "item", item: deItem(c.item)};
    if(c.kind === "block") return {kind: "block", block: deBlock(c.block, legacy)};
    return {kind: "stick", stick: deStick(c.stick, legacy)};
}

function serItem(it: MdFrameItem): SerFrameItem {
    // only a secondary door slot has a width of its own; the primary's follows innerWidth.
    if(it.kind === "doors") return {id: it.id, profile: it.profile, kind: "doors", width: it.width.isCalculated() ? undefined : serFrmVar(it.width)};
    if(it.kind === "threshold") return {id: it.id, kind: "threshold"};
    const size = it.size ? serFrmVar(it.size) : undefined;
    if(it.kind === "lite") return {id: it.id, profile: it.profile, kind: "lite", size, mark: it.mark, glazing: it.glazing, glass: serGlass(it.glass)};
    if(it.kind === "void") return {id: it.id, kind: "void", size};
    return {id: it.id, profile: it.profile, kind: "fixedPanel", size, material: it.material, position: it.position};
}

// a "doors" item gets placeholder width/height and a "threshold" item a placeholder
// size — wireDoorsItem re-binds them to the live vars after the tree is built.
function deItem(it: SerFrameItem): MdFrameItem {
    if(it.kind === "doors") {
        const width = it.width ? deVar(it.id + ":w", it.width) : new FrmVar(it.id + ":w", 0);
        return {id: it.id, profile: it.profile, kind: "doors", width, height: new FrmVar(it.id + ":h", 0)};
    }
    if(it.kind === "threshold") return {id: it.id, kind: "threshold", size: new FrmVar(it.id + ":size", 0)};
    const size = it.size ? deVar(it.id + ":size", it.size) : undefined;
    if(it.kind === "lite") return {id: it.id, profile: it.profile, kind: "lite", size, mark: it.mark, glazing: it.glazing, glass: deGlass(it.glass)};
    if(it.kind === "void") return {id: it.id, kind: "void", size};
    return {id: it.id, profile: it.profile, kind: "fixedPanel", size, material: it.material, position: it.position};
}

function serStick(s: MdFrameStick): SerFrameStick {
    return {
        id: s.id,
        thickness: s.thickness ? serFrmVar(s.thickness) : undefined,
        airGap: s.airGap || undefined,
        terminateA: s.terminateA,
        terminateB: s.terminateB,
        profileLeft: s.profileLeft,
        profileRight: s.profileRight,
        joinedTo: s.joinedTo,
        slope: s.slope ? {shortEnd: s.slope.shortEnd, drop: serFrmVar(s.slope.drop)} : undefined,
        angle: s.angle ? serScalar(s.angle) : undefined,
        anchors: s.anchors ? {sections: s.anchors.sections.map(a => ({...a}))} : undefined,
    };
}

function deStick(s: SerFrameStick, legacy: LegacyInfo): MdFrameStick {
    if(s.variant === "threshold") legacy.thresholdSticks.add(s.id);
    // a blob written while the slope was still called "angle" keeps it under that key.
    const slope = s.slope ?? (s.angle && "shortEnd" in s.angle ? s.angle : undefined);
    const angle = s.angle && "value" in s.angle ? s.angle : undefined;
    return {
        id: s.id,
        thickness: s.thickness ? deVar(s.id + ":thick", s.thickness) : undefined,
        airGap: s.airGap || undefined,
        terminateA: s.terminateA,
        terminateB: s.terminateB,
        profileLeft: s.profileLeft,
        profileRight: s.profileRight,
        joinedTo: s.joinedTo,
        slope: slope ? {shortEnd: slope.shortEnd, drop: deVar(s.id + ":drop", slope.drop)} : undefined,
        angle: angle ? deScalar(s.id + ":angle", angle) : undefined,
        anchors: s.anchors && s.anchors.sections.length > 0 ? {sections: s.anchors.sections.map(a => ({...a}))} : undefined,
    };
}

function deLegacyPerimeter(p: SerLegacyFramePerimeter, legacy: LegacyInfo): LegacyPerimeter {
    const b = p.bottom;
    const bottom = !b ? undefined
        : "kind" in b && b.kind === "split"
            ? {kind: "split" as const, left: deStick(b.left, legacy), threshold: deStick(b.threshold, legacy), right: deStick(b.right, legacy)}
            : deStick(b as SerFrameStick, legacy);
    return {
        top: p.top ? deStick(p.top, legacy) : undefined,
        bottom,
        left: p.left ? deStick(p.left, legacy) : undefined,
        right: p.right ? deStick(p.right, legacy) : undefined,
    };
}

// Reset the profile-dependent frame-cut properties to the defaults implied by the
// current profile (spec.profile) and base measurements (jambDepth, doorThickness).
// Shared by fromOpening (initial build, emit=false) and MdFrameSpec.setProfile (live
// profile switch, emit=true), so switching profiles resets exactly the fields a
// fresh load would set.
//
// throatSize is intentionally not set here: it's a calculated var
// (jambDepth - topTab - bottomTab), so resetting the tabs re-derives it automatically.
function applyProfileDefaults(spec: MdFrameSpec, emit: boolean) {
    const p = spec.doorFrameProfile;

    // door rabbet tracks the door thickness (+3/16" clearance)
    p.rabbetDepth.setInitial(p.doorThickness.get() + (3/16 * inches), emit);

    // both rabbets and the stop between them must fit inside the jamb depth. The
    // door rabbet is fixed by the door, so a thin jamb shrinks the bottom rabbet.
    const bottomRabbetRoom = spec.jambDepth.get() - p.rabbetDepth.get() - MIN_FLEX_LENGTH;
    p.bottomRabbetDepth.setInitial(Math.max(MIN_FLEX_LENGTH, Math.min(DEFAULT_BOTTOM_RABBET_DEPTH, bottomRabbetRoom)), emit);

    // tabs: profiles without tabs get none; tabbed profiles get a size keyed off the
    // jamb depth (a 5 3/4" jamb → 7/16" tab, otherwise 1/2").
    p.bottomTab.setInitial(0, emit);
    p.topTab.setInitial(0, emit);

    if(frameProfileHasTabs.has(spec.profile)) {
        const specialSize = (5 + 3/4) * inches;
        if(Math.abs(spec.jambDepth.get() - specialSize) < 1/16 * inches) {
            p.bottomTab.setInitial(7/16 * inches, emit);
            p.topTab.setInitial(7/16 * inches, emit);
        } else {
            p.topTab.setInitial(1/2 * inches, emit);
            p.bottomTab.setInitial(1/2 * inches, emit);
        }
    }

    // profile-specific stop/lip sizes reset to their defaults (wiping user overrides).
    p.drywallLip.setInitial(DEFAULT_DRYWALL_LIP, emit);
    p.drywallHookDrop.setInitial(DEFAULT_DRYWALL_HOOK_DROP, emit);
    p.kerfDepth.setInitial(DEFAULT_KERF_DEPTH, emit);
    p.expFace.setInitial(DEFAULT_EXP_FACE, emit);
}


export type MdFrameProfileType =
    | "STD" // Standard
    | "STD 2 5/8\"" // Standard w/ 2 5/8" Header
    | "EXP" // Expandable
    | "TB" // Thermally Broken
    | "CO" // Cased Open
    | "DE" // Double Egress
    | "FF" // Flush Fit
    | "DW" // Drywall (DBL Bend)
    | "SL" // Shadow Line
    | "SR" // Single Rabbet
    | "KF" // Kerf
    | "HS" // Hospital Stop
    | "PO" // Pocket
    | "COM"; // Communicating

// human-readable names for each profile code, for pickers (see menubar/EditGroups).
export const frameProfileLabels: Record<MdFrameProfileType, string> = {
    "STD": "Standard",
    "STD 2 5/8\"": "Standard (2 5/8\" Header)",
    "EXP": "Expandable",
    "TB": "Thermally Broken",
    "CO": "Cased Open",
    "DE": "Double Egress",
    "FF": "Flush Fit",
    "DW": "Drywall (Dbl Bend)",
    "SL": "Shadow Line",
    "SR": "Single Rabbet",
    "KF": "Kerf",
    "HS": "Hospital Stop",
    "PO": "Pocket",
    "COM": "Communicating",
};

export const frameProfileHasTabs = new Set<MdFrameProfileType>([
    "CO",
    "DE",
    "DW",
    "EXP",
    "KF",
    "SL",
    "SR",
    "TB",
    "STD",
    "STD 2 5/8\"",
]);


// mirrors the `value`s in allHandings (src/pages/project/quote/openings/QuoteOpenings.tsx)
export type MdDoorHanding =
    // single hinged leaves
    | "LH" | "RH" | "LHR" | "RHR"
    | "LH-DA" | "RH-DA"
    // active/inactive leaf designations
    | "LHA" | "RHA" | "LHRA" | "RHRA" | "LHA-DA" | "RHA-DA"
    // pairs
    | "LHA/RHA" | "LHRA/RHRA" | "LHA/RHA-DA"
    // double egress
    | "L-LHR" | "R-RHR" | "LHR/LHR" | "RHR/RHR"
    // communicating
    | "LHLH-COMM" | "RHRH-COMM" | "LHRH-COMM"
    // specialty (no hinge swing)
    | "POCKET" | "BYPASS" | "BIFOLD" | "CO"
    | "LH-POCKET" | "RH-POCKET" | "LH-SLIDE" | "RH-SLIDE"
    | "LH-BIFOLD" | "RH-BIFOLD" | "LH-BARN" | "RH-BARN"
    | "DBL POCKET" | "DBL BYPASS" | "DBL BIFOLD";

// handings made up of two leaves (an active + inactive leaf)
const pairHandings: ReadonlySet<MdDoorHanding> = new Set([
    "LHA/RHA", "LHRA/RHRA", "LHA/RHA-DA",
    "L-LHR", "R-RHR", "LHR/LHR", "RHR/RHR",
    "LHLH-COMM", "RHRH-COMM", "LHRH-COMM",
    "DBL POCKET", "DBL BYPASS", "DBL BIFOLD",
]);

export function isPairHanding(handing: MdDoorHanding) {
    return pairHandings.has(handing);
}
